/// <reference types="google.maps" />
import * as React from 'react';
import { ComboBox as AriaComboBox, ListBox as AriaListBox } from 'react-aria-components';

import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { MarkerPinIcon } from '../../icons';
import { loadPlacesLibrary } from '../../lib/googleMaps/loader';
import { parseAddress } from '../../lib/googleMaps/parseAddress';
import { cx } from '../../utils/cx';
import HintText from '../Input/HintText';
import { InputBase } from '../Input/Input';
import Label from '../Input/Label';
import Popover from '../Select/Popover';
import { SelectContext } from '../Select/Select';
import SelectItem from '../Select/SelectItem';

import type { IParsedAddress } from '../../lib/googleMaps/parseAddress';

export interface IAddressSuggestion {
  id: string;
  primary: string;
  secondary: string;
}

export interface IAddressAutocompleteProps {
  /** Google Maps JavaScript API key. Required — load this from your app env. */
  apiKey: string;
  /**
   * Called when the user picks a suggestion and Google returns its details.
   * Partial results (city / region / postal code) populate only the fields
   * Google has — `streetNumber` and `streetName` are undefined for those.
   */
  onSelect: (address: IParsedAddress) => void;
  /** Controlled input text. Pair with `onInputChange`. */
  value?: string;
  /** Initial input text when uncontrolled. */
  defaultValue?: string;
  /** Fires on every keystroke with the current input text. */
  onInputChange?: (input: string) => void;
  /**
   * Restrict suggestions to specific Place types (up to 5). Unset by default,
   * which returns addresses, cities, regions, and postal codes — matching the
   * "partial OR full address" requirement.
   *
   * See https://developers.google.com/maps/documentation/places/web-service/place-types
   */
  includedPrimaryTypes?: string[];
  /** CLDR two-letter region codes (e.g. ['us', 'ca']) to bias suggestions. */
  includedRegionCodes?: string[];
  /** Debounce in ms between keystrokes and the autocomplete request. */
  debounceMs?: number;
  /** Standard form-field props passed through to the underlying input. */
  label?: string;
  hint?: React.ReactNode;
  placeholder?: string;
  isInvalid?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  /** Input chrome size — matches the shared `Input` / `Select` primitives. */
  size?: 'sm' | 'md';
  name?: string;
  className?: string;
  popoverClassName?: string;
  /** Class for the inner `<input>` — e.g. right padding to clear an overlaid trailing action. */
  inputClassName?: string;
  /** Optional override for the empty / loading / error empty-state text. */
  emptyStateText?: string;
}

const DEFAULT_DEBOUNCE_MS = 200;

type FetchState = 'idle' | 'loading' | 'error';

/**
 * Typeahead address picker backed by the Google Places API (New).
 *
 * Renders a searchable combobox that, on selection, fetches structured Place
 * details and surfaces them via `onSelect`. Suggestion fetching is debounced
 * and uses an `AutocompleteSessionToken` per pick so Google bills the session
 * as a single transaction instead of N independent predictions.
 *
 * Built on shared design-system primitives — `InputBase` (input chrome),
 * `Popover` (dropdown chrome), `SelectItem` (list rows) — so any tweak to
 * those flows through automatically.
 *
 * `apiKey` is captured once per page (Google's JS API cannot rotate keys
 * after load) — passing a different key after first render is a no-op. Treat
 * `value` as fully controlled or fully uncontrolled; toggling the prop
 * between `undefined` and a string at runtime is not supported.
 */
const AddressAutocomplete = ({
  apiKey,
  onSelect,
  value,
  defaultValue,
  onInputChange,
  includedPrimaryTypes,
  includedRegionCodes,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  label,
  hint,
  placeholder = '123 Main St. San Francisco, CA',
  isInvalid,
  isDisabled,
  isRequired,
  size = 'md',
  name,
  className,
  popoverClassName,
  inputClassName,
  emptyStateText,
}: IAddressAutocompleteProps) => {
  const [inputValue, setInputValue] = React.useState(value ?? defaultValue ?? '');
  const [suggestions, setSuggestions] = React.useState<IAddressSuggestion[]>([]);
  const [fetchState, setFetchState] = React.useState<FetchState>('idle');
  const debouncedInput = useDebouncedValue(inputValue, debounceMs);

  const triggerRef = React.useRef<HTMLDivElement>(null);
  // Holds the live PlacePrediction objects so we can call `.toPlace()` on
  // selection without round-tripping the input through Google again.
  const predictionsRef = React.useRef(new Map<string, google.maps.places.PlacePrediction>());
  const sessionTokenRef = React.useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const placesLibRef = React.useRef<google.maps.PlacesLibrary | null>(null);
  // Tracks the latest request so stale responses don't overwrite a newer list.
  const requestSeqRef = React.useRef(0);
  // The last value the USER typed. The debounced-fetch effect only queries
  // Google when the settled input matches it — so a controlled `value` set
  // programmatically (initial pre-fill, async hydration of a saved address, or
  // the formatted string written back on selection) never fires a search; only
  // real keystrokes do. This is what keeps a settings card full of pre-filled
  // addresses from spraying autocomplete requests the moment it mounts.
  const lastTypedRef = React.useRef<string | null>(null);
  // Array props kept in refs so a caller passing an inline `[...]` literal
  // doesn't recreate `fetchSuggestions` every render — which would otherwise
  // re-run the fetch effect on every parent re-render.
  const includedPrimaryTypesRef = React.useRef(includedPrimaryTypes);
  includedPrimaryTypesRef.current = includedPrimaryTypes;
  const includedRegionCodesRef = React.useRef(includedRegionCodes);
  includedRegionCodesRef.current = includedRegionCodes;

  React.useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  const ensureLib = React.useCallback(async () => {
    if (!placesLibRef.current) {
      placesLibRef.current = await loadPlacesLibrary(apiKey);
    }
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new placesLibRef.current.AutocompleteSessionToken();
    }
    return placesLibRef.current;
  }, [apiKey]);

  // Warm the Places SDK on mount so the first keystroke doesn't pay the
  // one-time cold library-load latency. The loader memoizes globally, so the
  // first real fetch reuses this promise; a load failure surfaces on that fetch.
  React.useEffect(() => {
    void loadPlacesLibrary(apiKey).catch(() => {});
  }, [apiKey]);

  const fetchSuggestions = React.useCallback(
    async (input: string) => {
      const seq = ++requestSeqRef.current;
      if (!input.trim()) {
        setSuggestions([]);
        predictionsRef.current.clear();
        setFetchState('idle');
        return;
      }

      setFetchState('loading');
      try {
        const lib = await ensureLib();
        const primaryTypes = includedPrimaryTypesRef.current;
        const regionCodes = includedRegionCodesRef.current;
        const { suggestions: results } = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: sessionTokenRef.current ?? undefined,
          ...(primaryTypes ? { includedPrimaryTypes: primaryTypes } : {}),
          ...(regionCodes ? { includedRegionCodes: regionCodes } : {}),
        });

        // Ignore responses that arrive after a newer request started.
        if (seq !== requestSeqRef.current) {
          return;
        }

        const next: IAddressSuggestion[] = [];
        predictionsRef.current.clear();
        for (const suggestion of results) {
          const prediction = suggestion.placePrediction;
          if (!prediction) {
            continue;
          }
          next.push({
            id: prediction.placeId,
            primary: prediction.mainText?.text ?? prediction.text.text,
            secondary: prediction.secondaryText?.text ?? '',
          });
          predictionsRef.current.set(prediction.placeId, prediction);
        }
        setSuggestions(next);
        setFetchState('idle');
      } catch {
        if (seq !== requestSeqRef.current) {
          return;
        }
        setSuggestions([]);
        setFetchState('error');
      }
    },
    [ensureLib]
  );

  React.useEffect(() => {
    // Only the user's own typing should query Google — never a programmatic
    // `value` change (pre-fill, async hydration, or the selected address echoed back).
    if (debouncedInput !== lastTypedRef.current) {
      return;
    }
    void fetchSuggestions(debouncedInput);
  }, [debouncedInput, fetchSuggestions]);

  const handleInputChange = (next: string) => {
    lastTypedRef.current = next;
    setInputValue(next);
    onInputChange?.(next);
  };

  const handleSelectionChange = async (key: React.Key | null) => {
    if (key == null) {
      return;
    }
    const prediction = predictionsRef.current.get(String(key));
    if (!prediction) {
      return;
    }

    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ['id', 'formattedAddress', 'addressComponents', 'location'],
      });
      const parsed = parseAddress(place);
      setInputValue(parsed.formattedAddress);
      onInputChange?.(parsed.formattedAddress);
      onSelect(parsed);
    } catch {
      setFetchState('error');
    } finally {
      // Per Google guidance, a session token is single-use across one full
      // autocomplete + fetchFields cycle. Reset so the next session is billed
      // (and tokenized) independently.
      sessionTokenRef.current = null;
      predictionsRef.current.clear();
      setSuggestions([]);
    }
  };

  const emptyState =
    fetchState === 'loading'
      ? 'Searching…'
      : fetchState === 'error'
        ? 'Could not load suggestions.'
        : (emptyStateText ?? (inputValue.trim() ? 'No matches found.' : 'Start typing to see suggestions.'));

  return (
    <SelectContext.Provider value={{ size }}>
      <AriaComboBox
        aria-label={label ? undefined : placeholder}
        menuTrigger="input"
        allowsCustomValue
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onSelectionChange={handleSelectionChange}
        items={suggestions}
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        isRequired={isRequired}
        name={name}
        className={cx('flex w-full flex-col gap-1.5', className)}
      >
        {label && <Label isRequired={isRequired}>{label}</Label>}

        <InputBase
          groupRef={triggerRef}
          icon={MarkerPinIcon}
          placeholder={placeholder}
          isInvalid={isInvalid}
          isDisabled={isDisabled}
          size={size}
          inputClassName={inputClassName}
        />

        {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}

        <Popover size={size} triggerRef={triggerRef} className={popoverClassName}>
          <AriaListBox
            items={suggestions}
            className="outline-hidden size-full"
            renderEmptyState={() => <div className="text-md text-tertiary px-3.5 py-2.5">{emptyState}</div>}
          >
            {(item) => <SelectItem id={item.id} label={item.primary} supportingText={item.secondary || undefined} />}
          </AriaListBox>
        </Popover>
      </AriaComboBox>
    </SelectContext.Provider>
  );
};

AddressAutocomplete.displayName = 'AddressAutocomplete';

export default AddressAutocomplete;
